import type { CommercialTemplateId, DecorationAnchor, Plot, StaticCollider, WorldProp } from "@townsy/contracts";
import { YAW_EAST, YAW_NORTH, YAW_SOUTH, YAW_WEST, aabb, aabbAt } from "./geometry";
import { COMMERCIAL_TEMPLATES, HOME_MAX_FOOTPRINT, STOREFRONT_SIGN_Y, commercialStageInfo } from "./templates";

/**
 * Plot and prop-collider builders shared by every district (world v5, ADR-0041): Central (manifest.ts) and the
 * shopping districts (districts/shoppingDistrict.ts) build their shops, home plots and prop colliders with exactly
 * the same geometry. Moved unchanged out of manifest.ts (worldV5.test.ts proves every v4 plot and collider is
 * identical); the district is a trailing parameter that defaults to Central.
 */
export const CENTRAL_DISTRICT_ID = "central";

export const ROAD_HALF = 3; // 6 m road
export const SIDEWALK = 4; // 4 m sidewalks
export const PLOT_W = 12;
export const PLOT_D = 16;
export const COMMERCIAL_FRONT_Z = ROAD_HALF + SIDEWALK; // 7: from a street's centre line to the shop plots' front line
export const COMMERCIAL_PITCH = 14;
/** Distance from the building wall to the "Enter" spot / exit spawn on the sidewalk. */
export const ENTRANCE_SETBACK = 2.9;
const DOOR_HALF_WIDTH = 1.1;

// ---- Residential lanes --------------------------------------------------------------------------
export const SIDE_ROAD_HALF = 3;
export const RES_PLOT = 20;
const RES_HOME_DEPTH_FROM_FRONT = 7; // home centre offset from the plot front edge

export function pad(n: number): string {
  return String(n).padStart(2, "0");
}

/**
 * One commercial plot. North-side plots (the v2 geometry, unchanged) front onto the north sidewalk
 * and face south; south-side plots mirror them across the street and face north. `streetZ` is the
 * centre line of the street the plot fronts onto (Main Street z = 0; world v4: Market Street z = 70;
 * world v5: River Street and High Street z = 70).
 */
export function commercialPlot(n: number, cx: number, side: "north" | "south", styleId: CommercialTemplateId, address: string, streetZ = 0, districtId = CENTRAL_DISTRICT_ID): Plot {
  const stage0 = commercialStageInfo(0);
  const id = `c-${pad(n)}`;
  const style = COMMERCIAL_TEMPLATES[styleId];
  const fw = style.footprint.width;
  const fd = style.footprint.depth;
  const north = side === "north";
  const frontZ = north ? streetZ + COMMERCIAL_FRONT_Z : streetZ - COMMERCIAL_FRONT_Z;
  const bounds = north ? aabb(cx - PLOT_W / 2, frontZ, cx + PLOT_W / 2, frontZ + PLOT_D) : aabb(cx - PLOT_W / 2, frontZ - PLOT_D, cx + PLOT_W / 2, frontZ);
  const buildBounds = north ? aabb(cx - fw / 2, frontZ + 0.5, cx + fw / 2, frontZ + 0.5 + fd) : aabb(cx - fw / 2, frontZ - 0.5 - fd, cx + fw / 2, frontZ - 0.5);
  const wallZ = north ? buildBounds.minZ : buildBounds.maxZ;
  const out = north ? -1 : 1; // toward the street
  const facing = north ? YAW_SOUTH : YAW_NORTH;
  return {
    id,
    districtId,
    type: "COMMERCIAL",
    address,
    bounds,
    buildBounds,
    entrance: { position: { x: cx, z: wallZ + out * ENTRANCE_SETBACK }, facing: north ? YAW_NORTH : YAW_SOUTH },
    door: { position: { x: cx, z: wallZ }, direction: { x: 0, z: -out }, halfWidth: DOOR_HALF_WIDTH },
    facing,
    commercialTemplate: style.id,
    interiorTemplate: "store_room",
    signAnchors: [
      {
        kind: "storefront",
        position: { x: cx, y: STOREFRONT_SIGN_Y, z: wallZ + out * 0.06 },
        facing,
        width: style.storefrontSign.width,
        height: style.storefrontSign.height,
      },
      // Rooftop sign at stage 0; use skylineSignAnchor(plot, stage) for the live height.
      {
        kind: "skyline",
        position: { x: cx, y: stage0.heightMetres + style.rooftopSign.height / 2 + 0.3, z: wallZ - out * 1.0 },
        facing,
        width: style.rooftopSign.width,
        height: style.rooftopSign.height,
      },
    ],
    decorationAnchors: [],
    heightBand: 0,
  };
}

/**
 * One residential plot beside lane `sx` (the v2 geometry): west plots sit west of the lane and face
 * east; east plots face west. `n` is the id number, `address` its street address.
 */
export function residentialPlot(n: number, sx: number, side: "west" | "east", cz: number, address: string, districtId = CENTRAL_DISTRICT_ID): Plot {
  const id = `r-${pad(n)}`;
  const sidewalkEdge = side === "west" ? sx - SIDE_ROAD_HALF - SIDEWALK : sx + SIDE_ROAD_HALF + SIDEWALK;
  const minX = side === "west" ? sidewalkEdge - RES_PLOT : sidewalkEdge;
  const maxX = minX + RES_PLOT;
  const bounds = aabb(minX, cz - RES_PLOT / 2, maxX, cz + RES_PLOT / 2);
  const frontX = side === "west" ? maxX : minX;
  const homeCx = side === "west" ? frontX - RES_HOME_DEPTH_FROM_FRONT : frontX + RES_HOME_DEPTH_FROM_FRONT;
  const buildBounds = aabbAt(homeCx, cz, HOME_MAX_FOOTPRINT.depth, HOME_MAX_FOOTPRINT.width);
  const towardHome = side === "west" ? YAW_WEST : YAW_EAST;
  const frontFaceX = side === "west" ? buildBounds.maxX : buildBounds.minX;
  const entranceX = side === "west" ? sidewalkEdge + 1.6 : sidewalkEdge - 1.6;
  // Anchors in plot-local coordinates: u = metres in from the front edge, v = metres along the street.
  // The house (max footprint) occupies u 2..12, v -6.5..6.5; the front path (u 0..2, v -1.5..1.5) stays clear.
  const facing = side === "west" ? YAW_EAST : YAW_WEST;
  const inward = side === "west" ? -1 : 1;
  const at = (u: number, v: number) => ({ x: frontX + inward * u, z: cz + v });
  const QUARTERS = [0, Math.PI / 2, Math.PI, -Math.PI / 2];
  const decorationAnchors: DecorationAnchor[] = [
    { id: "back_large_a", type: "yard_large", position: at(16, -5), allowedRotations: [0, Math.PI / 2] },
    { id: "back_large_b", type: "yard_large", position: at(16, 5), allowedRotations: [0, Math.PI / 2] },
    { id: "side_medium", type: "yard_medium", position: at(7, -8.3), allowedRotations: QUARTERS },
    { id: "side_patio", type: "patio", position: at(6, 8.3), allowedRotations: [0, Math.PI / 2] },
    { id: "front_small_a", type: "yard_small", position: at(2.5, 8.6), allowedRotations: [0] },
    { id: "front_small_b", type: "yard_small", position: at(2.5, -8.6), allowedRotations: [0] },
    { id: "side_small_a", type: "yard_small", position: at(11, 8.6), allowedRotations: [0] },
    { id: "side_small_b", type: "yard_small", position: at(11, -8.6), allowedRotations: [0] },
    { id: "back_small_a", type: "yard_small", position: at(19, 9), allowedRotations: [0] },
    { id: "back_small_b", type: "yard_small", position: at(19, -9), allowedRotations: [0] },
    { id: "tree_a", type: "tree_spot", position: at(13.5, 8.8), allowedRotations: [0] },
    { id: "tree_b", type: "tree_spot", position: at(13.5, -8.8), allowedRotations: [0] },
    { id: "tree_back", type: "tree_spot", position: at(19.2, 0), allowedRotations: [0] },
    { id: "fence_front", type: "fence_line", position: at(0.3, 0), allowedRotations: [facing] },
  ];
  return {
    id,
    districtId,
    type: "RESIDENTIAL",
    address,
    bounds,
    buildBounds,
    entrance: { position: { x: entranceX, z: cz }, facing: towardHome },
    door: { position: { x: frontFaceX, z: cz }, direction: side === "west" ? { x: -1, z: 0 } : { x: 1, z: 0 }, halfWidth: 1.0 },
    facing,
    lotSign: { position: { x: side === "west" ? frontX - 1.2 : frontX + 1.2, z: cz + 6.5 }, facing },
    interiorTemplate: "home_room",
    signAnchors: [],
    decorationAnchors,
    heightBand: 0,
  };
}

/**
 * Prop collider footprints (metres, width along the prop's local x, depth along local z, times the
 * prop scale), re-checked against apps/web/src/scene/town/props.ts in round 3 (ADR-0006):
 *  - tree: trunk <= 0.55 m (x 1.14 jitter) -> 0.9 m box;
 *  - bench: 1.7 m seat, legs and back 0.55 m deep -> 1.8 x 0.6 turned with the bench (was a 1.6 m square);
 *  - lamp: 0.5 m base (0.25 m tall) -> 0.5 m (was 0.4, the pole only);
 *  - planter: 1.42 m rim -> 1.42 m (was 1.2);
 *  - fountain: 6.2 m round step (0.3 m tall) -> two crossed 6.2 x 4.4 boxes covering the circle (was a 5 m square).
 */
const PROP_COLLIDER_SIZE: Record<WorldProp["kind"], { width: number; depth: number }> = {
  tree: { width: 0.9, depth: 0.9 },
  bench: { width: 1.8, depth: 0.6 },
  lamp: { width: 0.5, depth: 0.5 },
  planter: { width: 1.42, depth: 1.42 },
  fountain: { width: 6.2, depth: 6.2 },
};
/** The fountain's crossed boxes: 6.2 m long and 4.4 m wide (4.4 >= 6.2 / sqrt 2 covers the round step). */
const FOUNTAIN_CROSS_WIDTH = 4.4;

/**
 * The collider boxes of prop number `i` of its district's prop list: ids `prop:<i>:<kind>` (Central, the v4 ids:
 * Central's props come first in the manifest, so `i` is also the manifest index) or, world v5 (ADR-0041),
 * `prop:<i>:<kind>:<districtId>` for another district (`i` counted in that district's own prop list, the index the
 * district slice and its bench seat ids use). `districtId` (when given) also tags the colliders.
 */
export function propColliders(p: WorldProp, i: number, districtId?: string): StaticCollider[] {
  const size = PROP_COLLIDER_SIZE[p.kind];
  const { x, z } = p.position;
  const suffix = districtId && districtId !== CENTRAL_DISTRICT_ID ? `:${districtId}` : "";
  const tag = districtId ? { districtId } : {};
  if (p.kind === "fountain") {
    const d = size.width * p.scale;
    const w = FOUNTAIN_CROSS_WIDTH * p.scale;
    return [
      { id: `prop:${i}:fountain${suffix}`, kind: "prop", aabb: aabbAt(x, z, d, w), ...tag },
      { id: `prop:${i}:fountain_b${suffix}`, kind: "prop", aabb: aabbAt(x, z, w, d), ...tag },
    ];
  }
  const quarter = Math.round(Math.abs(p.rotation) / (Math.PI / 2)) % 2 === 1;
  const w = (quarter ? size.depth : size.width) * p.scale;
  const d = (quarter ? size.width : size.depth) * p.scale;
  return [{ id: `prop:${i}:${p.kind}${suffix}`, kind: "prop", aabb: aabbAt(x, z, w, d), ...tag }];
}
